import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
} from '@mui/material';
import Modal from 'react-modal';

/**
 * How long the other admin has held this case, in Thai. The backend has always
 * sent `currentAdmin.takenAt`; it was never shown, so whoever was deciding
 * whether to force-take had nothing to judge by (I22).
 *
 * That judgement matters because the two admins can end up buying the same item
 * twice on Mercari: a case taken a minute ago means someone is working it right
 * now, while one taken an hour ago is almost certainly a closed tab.
 */
const heldFor = (takenAt) => {
  if (!takenAt) return null;
  const minutes = Math.floor((Date.now() - new Date(takenAt).getTime()) / 60000);
  if (!Number.isFinite(minutes) || minutes < 0) return null;
  if (minutes < 1) return 'ไม่ถึง 1 นาที';
  if (minutes < 60) return `${minutes} นาที`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} ชั่วโมง ${rest} นาที` : `${hours} ชั่วโมง`;
};

const ForceTakeModal = ({
  isOpen,
  onClose,
  currentAdmin,
  onForceTake,
  isLoading,
  style
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={style}
      ariaHideApp={false}
    >
      <Paper elevation={0} sx={{ p: 4, borderRadius: 2 }}>
        <Box>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            เคสนี้ถูกดำเนินการโดยผู้ดูแลอื่น
          </Typography>
          <Typography variant="h6" sx={{ mb: 1, mt:4, color: '#4a4a4a', fontSize: '1.1rem' }}>
            เคสนี้กำลังถูกดำเนินการโดย: <strong>{currentAdmin?.nickname || 'ผู้ดูแลอื่น'}</strong>
          </Typography>
          {heldFor(currentAdmin?.takenAt) && (
            <Typography variant="h6" sx={{ mb: 2, color: '#4a4a4a', fontSize: '1.1rem' }}>
              ถือเคสนี้มาแล้ว: <strong>{heldFor(currentAdmin.takenAt)}</strong>
            </Typography>
          )}
          <Typography variant="body1" sx={{ mb: 2, color: '#b26a00', fontSize: '1rem' }}>
            ถ้าเพิ่งถือไม่นาน แอดมินคนนั้นอาจกำลังกดซื้อบน Mercari อยู่ กรุณาติดต่อกันก่อนรับเคสมาแทน
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, color: '#666666', fontSize: '1rem' }}>
            คุณต้องการที่จะรับเคสนี้มาแทนหรือไม่?
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={isLoading}
              sx={{
                minWidth: '120px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '1rem',
                borderColor: '#e0e0e0',
                '&:hover': {
                  borderColor: '#bdbdbd',
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              ยกเลิก
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={onForceTake}
              disabled={isLoading}
              sx={{
                minWidth: '120px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '1rem',
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: 'none',
                  backgroundColor: '#1976d2'
                }
              }}
            >
              {isLoading ? <CircularProgress size={24} /> : 'รับเคสนี้มาแทน'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default ForceTakeModal;